import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  CircularProgress,
  Chip,
  Divider,
  Stack,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../../services/api';
import { getCustomers, getCustomer } from '../../services/customer.service';
import PageHeader from '../../components/common/PageHeader';
import { useNotification } from '../../contexts/NotificationContext';
import { getPlans } from '../../services/subscription.service';
import useOrganizationParams from '../../hooks/useOrganizationParams';
import { getSubscription } from '../../services/subscription.service';

const ITEM_TYPES = {
  SUBSCRIPTION: 'subscription',
  CREDITS: 'credits'
};

const emptyItem = {
  description: '',
  planId: '',
  planName: '',
  billingCycle: 'monthly',
  quantity: 1,
  unitPrice: 0,
  type: ITEM_TYPES.SUBSCRIPTION,
};

const CreateInvoice = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const organizationParams = useOrganizationParams();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    customerId: '',
    items: [{ ...emptyItem }],
    dueDate: null,
    status: 'draft',
    currency: 'usd',
    billingDetails: {
      name: '',
      email: '',
      address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
      },
    },
  });

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [plans, setPlans] = useState([]);
  const [planSearchLoading, setPlanSearchLoading] = useState(false);
  const [planSearchQuery, setPlanSearchQuery] = useState('');
  const [customerSubscriptions, setCustomerSubscriptions] = useState([]);

  const searchCustomers = async (query) => {
    if (!query) {
      return [];
    }
    setSearchLoading(true);
    try {
      const response = await getCustomers({ search: query, ...organizationParams });
      if (response && response.success && Array.isArray(response.data)) {
        setCustomers(response.data);
      } else {
        console.error('Unexpected customer data format:', response);
        setCustomers([]);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
      showNotification('Error searching customers', 'error');
      setCustomers([]);
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        searchCustomers(searchQuery);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleCustomerChange = async (event, newValue) => {
    setSelectedCustomer(newValue);
    if (newValue) {
      setFormData(prev => ({ ...prev, customerId: newValue._id }));
      try {
        setFormData(prev => ({
          ...prev,
          customerId: newValue._id,
          billingDetails: {
            name: newValue.name,
            email: newValue.email,
            address: newValue.address || {
              line1: '',
              line2: '',
              city: '',
              state: '',
              postalCode: '',
              country: '',
            },
          },
        }));
      } catch (error) {
        console.error('Error setting customer details:', error);
        showNotification('Error setting customer details', 'error');
      }
    } else {
      setFormData(prev => ({
        ...prev,
        customerId: '',
        billingDetails: {
          name: '',
          email: '',
          address: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            postalCode: '',
            country: '',
          },
        },
      }));
    }
  };

  useEffect(() => {
    const fetchCustomerSubscriptions = async () => {
      if (formData.customerId) {
        try {
          const response = await getSubscription(formData.customerId);
          if (response && response.success) {
            setCustomerSubscriptions(Array.isArray(response.data) ? response.data : []);
          } else {
            setCustomerSubscriptions([]);
          }
        } catch (error) {
          console.error('Error fetching customer subscriptions:', error);
          setCustomerSubscriptions([]);
        }
      } else {
        setCustomerSubscriptions([]);
      }
    };
    fetchCustomerSubscriptions();
  }, [formData.customerId]);

  const searchPlans = async (query) => {
    if (!query) {
      setPlans([]);
      return;
    }
    setPlanSearchLoading(true);
    try {
      const response = await getPlans({ search: query, ...organizationParams });
      if (response && response.success) {
        const transformedPlans = response.data.flatMap(plan => {
          const entries = [
            {
              ...plan,
              billingCycle: 'monthly',
              displayPrice: plan.monthlyPrice,
              displayName: `${plan.name} (Monthly)`,
            },
            {
              ...plan,
              billingCycle: 'annual',
              displayPrice: plan.annualPrice,
              displayName: `${plan.name} (Annual)`,
            },
          ];

          if (plan.planType === 'lifetime') {
            entries.push({
              ...plan,
              billingCycle: 'lifetime',
              displayPrice: plan.lifetimePrice,
              displayName: `${plan.name} (Lifetime)`,
            });
          }

          return entries;
        });
        setPlans(transformedPlans);
      }
    } catch (error) {
      console.error('Error searching plans:', error);
      setPlans([]);
    } finally {
      setPlanSearchLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (planSearchQuery) {
        searchPlans(planSearchQuery);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [planSearchQuery]);

  const handleItemChange = (index, field, value, planDetails = null) => {
    const newItems = [...formData.items];
    if (planDetails) {
      newItems[index] = {
        ...newItems[index],
        description: planDetails.displayName,
        planId: planDetails._id,
        planName: planDetails.name,
        billingCycle: planDetails.billingCycle,
        unitPrice: planDetails.displayPrice || 0,
        quantity: 1,
        amount: planDetails.displayPrice || 0,
        selectedPlan: planDetails,
      };
    } else {
      const quantity = field === 'quantity' ? (value || 0) : (newItems[index].quantity || 0);
      const unitPrice = field === 'unitPrice' ? (value || 0) : (newItems[index].unitPrice || 0);
      const amount = quantity * unitPrice;

      newItems[index] = {
        ...newItems[index],
        [field]: value || 0,
        quantity: field === 'quantity' ? (value || 0) : (newItems[index].quantity || 0),
        unitPrice: field === 'unitPrice' ? (value || 0) : (newItems[index].unitPrice || 0),
        amount,
        description: newItems[index].type === ITEM_TYPES.CREDITS ? 'Additional Credits' : newItems[index].description,
      };
    }
    setFormData((prev) => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { ...emptyItem }],
    }));
  };

  const removeItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 0;
      const unitPrice = Number(item.unitPrice) || 0;
      return sum + (quantity * unitPrice);
    }, 0);
    return {
      subtotal,
      total: subtotal, // Add tax calculation if needed
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const { subtotal, total } = calculateTotals();
      
      // Get organizationId from JWT token
      const token = localStorage.getItem('token');
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
      const organizationId = decodedToken.organizationId;

      // Generate invoice number with organization prefix
      const timestamp = new Date().getTime();
      const randomSuffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      const invoiceNumber = `INV-${timestamp}-${randomSuffix}`;

      // Clean up items before submission
      const cleanedItems = formData.items.map(item => {
        const cleanedItem = {
          description: item.type === ITEM_TYPES.CREDITS ? 'Additional Credits' : item.description,
          quantity: Number(item.quantity) || 0,
          unitPrice: Number(item.unitPrice) || 0,
          type: item.type,
          amount: Number(item.amount) || 0,
        };

        // Only add plan-related fields if it's a subscription item
        if (item.type === ITEM_TYPES.SUBSCRIPTION && item.selectedPlan) {
          cleanedItem.planId = item.selectedPlan._id;
          cleanedItem.planName = item.selectedPlan.name;
          cleanedItem.billingCycle = item.selectedPlan.billingCycle;
        }

        return cleanedItem;
      });

      const invoiceData = {
        customerId: formData.customerId,
        organizationId: organizationId,
        number: invoiceNumber,
        items: cleanedItems,
        dueDate: formData.dueDate,
        status: formData.status || 'draft',
        currency: formData.currency || 'usd',
        billingDetails: {
          name: formData.billingDetails.name || '',
          email: formData.billingDetails.email || '',
          address: {
            line1: formData.billingDetails.address.line1 || '',
            line2: formData.billingDetails.address.line2 || '',
            city: formData.billingDetails.address.city || '',
            state: formData.billingDetails.address.state || '',
            postalCode: formData.billingDetails.address.postalCode || '',
            country: formData.billingDetails.address.country || '',
          },
        },
        subtotal: Number(subtotal) || 0,
        total: Number(total) || 0,
        amountDue: Number(total) || 0,
        createdBy: decodedToken.id, // Add the user ID who created the invoice
      };

      // Log the data being sent
      console.log('Submitting invoice data:', JSON.stringify(invoiceData, null, 2));

      const response = await api.post('/invoices', invoiceData);

      if (response.data.success) {
        showNotification('Invoice created successfully', 'success');
        navigate(`/billing/invoices/${response.data.data._id}`);
      } else {
        throw new Error(response.data.message || 'Failed to create invoice');
      }
    } catch (error) {
      console.error('Error creating invoice:', error.response?.data || error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const { subtotal, total } = calculateTotals();

  const renderCustomerSubscriptions = () => {
    if (!formData.customerId || !Array.isArray(customerSubscriptions) || customerSubscriptions.length === 0) {
      return null;
    }

    return (
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Current Subscriptions
        </Typography>
        <Stack spacing={2}>
          {customerSubscriptions.map((subscription) => (
            <Box key={subscription._id}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1">{subscription.planName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {subscription.billingCycle === 'monthly'
                      ? 'Monthly'
                      : subscription.billingCycle === 'annual'
                      ? 'Annual'
                      : subscription.billingCycle === 'lifetime'
                      ? 'Lifetime'
                      : subscription.billingCycle}{' '}
                    Plan
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" align="right">
                    Status: <Chip
                      label={subscription.status}
                      size="small"
                      color={subscription.status === 'active' ? 'success' : 'default'}
                    />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="right">
                    Next billing: {subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
              <Divider sx={{ mt: 2 }} />
            </Box>
          ))}
        </Stack>
      </Card>
    );
  };

  const handleItemTypeChange = (index, type) => {
    const newItems = [...formData.items];
    newItems[index] = {
      ...emptyItem,
      type,
    };
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  return (
    <Box>
      <PageHeader title="Create Invoice" />

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    options={customers || []}
                    getOptionLabel={(option) => option.name || ''}
                    value={selectedCustomer}
                    onChange={handleCustomerChange}
                    onInputChange={(event, newInputValue) => {
                      setSearchQuery(newInputValue);
                    }}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    loading={searchLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Customer"
                        required
                        error={!formData.customerId}
                        helperText={!formData.customerId ? 'Please select a customer' : ''}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {searchLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <li {...otherProps} key={option._id}>
                          <Box>
                            <Typography variant="body1">{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.email}
                            </Typography>
                          </Box>
                        </li>
                      );
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  {renderCustomerSubscriptions()}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Items
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell width="15%">Type</TableCell>
                          <TableCell width="45%">Description</TableCell>
                          <TableCell align="right" width="10%">Quantity</TableCell>
                          <TableCell align="right" width="15%">Unit Price</TableCell>
                          <TableCell align="right" width="10%">Amount</TableCell>
                          <TableCell align="right" width="5%">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                value={item.type}
                                onChange={(e) => handleItemTypeChange(index, e.target.value)}
                                required
                              >
                                <MenuItem value={ITEM_TYPES.SUBSCRIPTION}>Subscription</MenuItem>
                                <MenuItem value={ITEM_TYPES.CREDITS}>Additional Credits</MenuItem>
                              </TextField>
                            </TableCell>
                            <TableCell>
                              {item.type === ITEM_TYPES.SUBSCRIPTION ? (
                                <Autocomplete
                                  fullWidth
                                  size="small"
                                  options={plans}
                                  getOptionLabel={(option) => option.displayName || ''}
                                  value={item.selectedPlan || null}
                                  onChange={(event, newValue) => handleItemChange(index, 'plan', null, newValue)}
                                  onInputChange={(event, newValue) => {
                                    setPlanSearchQuery(newValue);
                                  }}
                                  loading={planSearchLoading}
                                  isOptionEqualToValue={(option, value) => {
                                    return option._id === value._id && option.billingCycle === value.billingCycle;
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Search plans..."
                                      required
                                      InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                          <>
                                            {planSearchLoading ? (
                                              <CircularProgress color="inherit" size={20} />
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                          </>
                                        ),
                                      }}
                                    />
                                  )}
                                  renderOption={(props, option) => {
                                    const isSubscribed = Array.isArray(customerSubscriptions) && customerSubscriptions.some(
                                      sub => sub.planId === option._id && sub.billingCycle === option.billingCycle
                                    );
                                    const { key, ...otherProps } = props;
                                    return (
                                      <li {...otherProps} key={`${option._id}-${option.billingCycle}`}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', py: 1 }}>
                                          <Box>
                                            <Typography variant="body1">{option.displayName}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: 'USD',
                                              }).format(option.displayPrice)}
                                            </Typography>
                                          </Box>
                                          {isSubscribed && (
                                            <Chip
                                              label="Subscribed"
                                              size="small"
                                              color="primary"
                                              sx={{ ml: 1 }}
                                            />
                                          )}
                                        </Box>
                                      </li>
                                    );
                                  }}
                                />
                              ) : (
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Credits Amount"
                                  type="number"
                                  value={item.unitPrice}
                                  onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value))}
                                  required
                                  inputProps={{ min: 1 }}
                                />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                value={item.quantity || ''}
                                onChange={(e) => {
                                  const value = e.target.value === '' ? 0 : Number(e.target.value);
                                  handleItemChange(index, 'quantity', value);
                                }}
                                inputProps={{ min: 1 }}
                                required
                                sx={{ width: '80px' }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {item.type === ITEM_TYPES.SUBSCRIPTION ? (
                                <Typography>
                                  {new Intl.NumberFormat('en-US', {
                                    style: 'currency',
                                    currency: formData.currency,
                                  }).format(item.unitPrice || 0)}
                                </Typography>
                              ) : (
                                <TextField
                                  type="number"
                                  size="small"
                                  value={item.unitPrice || ''}
                                  onChange={(e) => {
                                    const value = e.target.value === '' ? 0 : Number(e.target.value);
                                    handleItemChange(index, 'unitPrice', value);
                                  }}
                                  inputProps={{ min: 0, step: "0.01" }}
                                  required
                                  sx={{ width: '120px' }}
                                />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="medium">
                                {new Intl.NumberFormat('en-US', {
                                  style: 'currency',
                                  currency: formData.currency,
                                }).format(item.amount || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => removeItem(index)}
                                disabled={formData.items.length === 1}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ mt: 2 }}>
                    <Button 
                      startIcon={<AddIcon />} 
                      onClick={addItem}
                      disabled={formData.items.length >= 2}
                      variant="outlined"
                      size="small"
                    >
                      Add Item
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Invoice Details
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Due Date"
                      value={formData.dueDate}
                      onChange={(date) =>
                        setFormData((prev) => ({ ...prev, dueDate: date }))
                      }
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={formData.status}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, status: e.target.value }))
                    }
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="open">Open</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Billing Details
                  </Typography>
                  <TextField
                    fullWidth
                    label="Name"
                    value={formData.billingDetails.name}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        billingDetails: {
                          ...prev.billingDetails,
                          name: e.target.value,
                        },
                      }))
                    }
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.billingDetails.email}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        billingDetails: {
                          ...prev.billingDetails,
                          email: e.target.value,
                        },
                      }))
                    }
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Address Line 1"
                    value={formData.billingDetails.address.line1}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        billingDetails: {
                          ...prev.billingDetails,
                          address: {
                            ...prev.billingDetails.address,
                            line1: e.target.value,
                          },
                        },
                      }))
                    }
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Address Line 2"
                    value={formData.billingDetails.address.line2}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        billingDetails: {
                          ...prev.billingDetails,
                          address: {
                            ...prev.billingDetails.address,
                            line2: e.target.value,
                          },
                        },
                      }))
                    }
                    sx={{ mb: 2 }}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="City"
                        value={formData.billingDetails.address.city}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            billingDetails: {
                              ...prev.billingDetails,
                              address: {
                                ...prev.billingDetails.address,
                                city: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="State"
                        value={formData.billingDetails.address.state}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            billingDetails: {
                              ...prev.billingDetails,
                              address: {
                                ...prev.billingDetails.address,
                                state: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Postal Code"
                        value={formData.billingDetails.address.postalCode}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            billingDetails: {
                              ...prev.billingDetails,
                              address: {
                                ...prev.billingDetails.address,
                                postalCode: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Country"
                        value={formData.billingDetails.address.country}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            billingDetails: {
                              ...prev.billingDetails,
                              address: {
                                ...prev.billingDetails.address,
                                country: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Summary
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2">Subtotal:</Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="body2">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: formData.currency,
                          }).format(subtotal)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2">Total:</Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="subtitle2">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: formData.currency,
                          }).format(total)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={loading}
                      fullWidth
                    >
                      Create Invoice
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default CreateInvoice; 